import { formatDetailDate } from '../../../../helpers/date';
import {
  FundPerformanceData,
  FundPerformancePeriodKey,
} from '../../../../types/fundsummary.type';
import './FundPerformance.css';

export interface PerformanceRow {
  period: string;
  fund: number | null;
  benchmark: number | null;
}

export interface FundPerformanceProps {
  data: FundPerformanceData;
  updateDate: string;
}

const formatPercent = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const valueClass = (value: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'fund-performance__value--unchg';
  }
  if (value > 0) return 'fund-performance__value--up';
  if (value < 0) return 'fund-performance__value--down';
  return 'fund-performance__value--unchg';
};

const Bar = ({
  value,
  maxAbs,
  modifier,
}: {
  value: number | null;
  maxAbs: number;
  modifier: 'fund' | 'benchmark';
}): JSX.Element => {
  const pct =
    value && maxAbs > 0 ? Math.min((Math.abs(value) / maxAbs) * 100, 100) : 0;
  const isNegative = (value ?? 0) < 0;

  return (
    <div className="fund-performance__bar-row">
      <div className="fund-performance__bar-half fund-performance__bar-half--neg">
        {isNegative && (
          <span
            className={`fund-performance__bar-fill fund-performance__bar-fill--${modifier}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <div className="fund-performance__bar-half fund-performance__bar-half--pos">
        {!isNegative && pct > 0 && (
          <span
            className={`fund-performance__bar-fill fund-performance__bar-fill--${modifier}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  );
};

const periodsMap: { key: FundPerformancePeriodKey; label: string }[] = [
  { key: 'pctGrowthPrevDay1m', label: '1M' },
  { key: 'pctGrowthPrevDay3m', label: '3M' },
  { key: 'pctGrowthPrevDay6m', label: '6M' },
  { key: 'pctGrowthPrevDay1y', label: '1Y' },
  { key: 'pctGrowthPrevDay3y', label: '3Y' },
  { key: 'pctGrowthPrevDay5y', label: '5Y' },
  { key: 'pctGrowthPrevDay10y', label: '10Y' },
];

const FundPerformance = ({
  data,
  updateDate,
}: FundPerformanceProps): JSX.Element => {
  const performanceData = periodsMap.map(({ key, label }) => ({
    period: label,
    fund: data.periods.fund[key],
    benchmark: data.periods.fundBenchmark[key],
  }));
  const maxAbs = performanceData.reduce((max, row) => {
    const f = row.fund ?? 0;
    const b = row.benchmark ?? 0;
    return Math.max(max, Math.abs(f), Math.abs(b));
  }, 0);

  return (
    <div className="fund-performance">
      <div className="fund-performance__header">
        <span className="fund-performance__title">
          Total return performance ({formatDetailDate(updateDate, false, 'DMY')}
          )
        </span>
        <div className="fund-performance__legend">
          <span className="fund-performance__legend-item">
            <span className="fund-performance__swatch fund-performance__swatch--fund" />
            Fund
          </span>
          <span className="fund-performance__legend-item">
            <span className="fund-performance__swatch fund-performance__swatch--benchmark" />
            Benchmark
          </span>
        </div>
      </div>

      {performanceData.map((row) => (
        <div className="fund-performance__row" key={row.period}>
          <div className="fund-performance__period">{row.period}</div>
          <div className={`fund-performance__value ${valueClass(row.fund)}`}>
            {formatPercent(row.fund)}
          </div>
          <div
            className={`fund-performance__value ${valueClass(row.benchmark)}`}
          >
            {formatPercent(row.benchmark)}
          </div>
          <div className="fund-performance__bar">
            <Bar value={row.fund} maxAbs={maxAbs} modifier="fund" />
            <Bar value={row.benchmark} maxAbs={maxAbs} modifier="benchmark" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default FundPerformance;
